import {
  collection,
  doc,
  limit,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  type Unsubscribe,
} from '@firebase/firestore';
import { httpsCallable } from '@firebase/functions';
import { db, functions } from './firebase';
import { onSnapshotWithRetry } from './snapshotRetry';
import { COLLECTIONS } from './collections';
import { mapDocs } from '@utils/firestore';
import {
  MAX_BIDS_PER_BUNDLE,
  MAX_BUNDLES_PER_PLAN,
  PlanBidSchema,
} from '../schemas/firestoreSchemas';
import type { PlanBid } from '../types';
import { createPrefixedLogger } from '../utils/logger';

const log = createPrefixedLogger('api:planBids');

// Vendor bids on a plan's bundles (Oct 2026): defensePlans/{planId}/bids.
// Only the submitBid Cloud Function creates them (the vendor has no
// account); only the plan owner reads them, and the owner's one write is
// moving a bid to accepted or declined. Rules enforce all three.

function bidsCollection(planId: string) {
  return collection(db, COLLECTIONS.DEFENSE_PLANS, planId, COLLECTIONS.DEFENSE_PLAN_BIDS);
}

export const planBidsApi = {
  /** Every bid on the plan, oldest first. Owner only: rules deny anyone else. */
  subscribeBids(
    planId: string,
    onNext: (bids: PlanBid[]) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    const q = query(
      bidsCollection(planId),
      orderBy('createdAt', 'asc'),
      limit(MAX_BIDS_PER_BUNDLE * MAX_BUNDLES_PER_PLAN)
    );
    return onSnapshotWithRetry(
      q,
      (snap) => onNext(mapDocs(snap, PlanBidSchema)),
      (error) => {
        log.warn('subscribeBids failed:', error);
        onError?.(error);
      }
    );
  },

  /** The owner's answer. Price, words and contact stay the vendor's. */
  async setBidStatus(
    planId: string,
    bidId: string,
    status: 'accepted' | 'declined',
    acceptedPartyId?: string
  ): Promise<void> {
    await updateDoc(doc(bidsCollection(planId), bidId), {
      status,
      ...(acceptedPartyId ? { acceptedPartyId } : {}),
      updatedAt: serverTimestamp(),
    });
  },
};

export interface BidRequest {
  planId: string;
  bundleId: string;
  organization: string;
  person: string;
  email: string;
  phone?: string;
  /** Whole dollars, or null when the vendor gives no price. */
  amount: number | null;
  note?: string;
  /** Honeypot. Always '' from a real person; a bot that fills every field trips it. */
  website: string;
}

export interface BidResult {
  ok: true;
  /** True when this vendor had already bid on this bundle and the bid changed. */
  updated: boolean;
}

export type BidErrorKind = 'invalid' | 'closed' | 'full' | 'rate-limited' | 'unknown';

export interface BidError {
  kind: BidErrorKind;
  /** Plain copy, ready to show. */
  message: string;
}

const BID_ERROR_COPY: Record<BidErrorKind, string> = {
  invalid: 'Check the form, then try again.',
  closed: "This opportunity isn't open for bids.",
  full: 'This opportunity has all the bids it can take.',
  'rate-limited': 'Too many tries. Wait a little, then try again.',
  unknown: "That didn't go through. Try again in a moment.",
};

/**
 * Maps the callable's HttpsError onto one of our kinds. The server says
 * 'closed' for a private, missing or closed bundle alike, and 'full' at the
 * per-bundle cap, both on failed-precondition.
 */
export function toBidError(error: unknown): BidError {
  const code = (error as { code?: string } | null)?.code ?? '';
  const message = (error as { message?: string } | null)?.message ?? '';
  const bare = code.replace(/^functions\//, '');
  let kind: BidErrorKind;
  switch (bare) {
    case 'invalid-argument':
      kind = 'invalid';
      break;
    case 'not-found':
    case 'failed-precondition':
      kind = /full/i.test(message) ? 'full' : 'closed';
      break;
    case 'resource-exhausted':
      kind = 'rate-limited';
      break;
    default:
      kind = 'unknown';
  }
  // The function's invalid-argument messages are written for people, so
  // they beat the generic line. The rest we word.
  return { kind, message: kind === 'invalid' && message ? message : BID_ERROR_COPY[kind] };
}

/** Sends a vendor's bid. No account; the function checks everything again. */
export async function submitBid(request: BidRequest): Promise<BidResult> {
  try {
    const callable = httpsCallable<BidRequest, BidResult>(functions, 'submitBid');
    const result = await callable(request);
    return result.data;
  } catch (error) {
    const mapped = toBidError(error);
    if (mapped.kind === 'unknown') log.error('submitBid failed:', error);
    throw mapped;
  }
}
